import { GameState } from '../core/GameState';
import { eventBus } from '../core/EventBus';
import { ACHIEVEMENTS, AchievementDef } from '../data/achievements';
import { BUILDINGS } from '../data/buildings';

const BAKE_THRESHOLDS = [
  { id: 'wake_and_bake', amount: 1 },
  { id: 'making_some_dough', amount: 1_000 },
  { id: 'so_baked_right_now', amount: 100_000 },
  { id: 'fledgling_bakery', amount: 1e6 },
  { id: 'affluent_bakery', amount: 1e8 },
  { id: 'world_famous_bakery', amount: 1e10 },
  { id: 'cosmic_bakery', amount: 1e12 },
  { id: 'galactic_bakery', amount: 1e14 },
  { id: 'universal_bakery', amount: 1e16 },
  { id: 'timeless_bakery', amount: 1e18 },
  { id: 'infinite_bakery', amount: 1e20 },
  { id: 'immortal_bakery', amount: 1e22 },
];

const CLICK_THRESHOLDS = [
  { id: 'click', amount: 1 },
  { id: 'double_click', amount: 100 },
  { id: 'mouse_wheel', amount: 1_000 },
  { id: 'of_mice_and_men', amount: 10_000 },
  { id: 'the_digital', amount: 100_000 },
  { id: 'extreme_polydactyly', amount: 1_000_000 },
];

interface BuildingAchievThreshold {
  building: string;
  count: number;
  achievementId: string;
}

const BUILDING_THRESHOLDS: BuildingAchievThreshold[] = [
  { building: 'cursor', count: 1, achievementId: 'cursor_1' },
  { building: 'cursor', count: 50, achievementId: 'cursor_50' },
  { building: 'cursor', count: 100, achievementId: 'cursor_100' },
  { building: 'cursor', count: 150, achievementId: 'cursor_150' },
  { building: 'cursor', count: 200, achievementId: 'cursor_200' },
  { building: 'grandma', count: 1, achievementId: 'grandma_1' },
  { building: 'grandma', count: 50, achievementId: 'grandma_50' },
  { building: 'grandma', count: 100, achievementId: 'grandma_100' },
  { building: 'grandma', count: 150, achievementId: 'grandma_150' },
  { building: 'grandma', count: 200, achievementId: 'grandma_200' },
  { building: 'farm', count: 1, achievementId: 'farm_1' },
  { building: 'farm', count: 50, achievementId: 'farm_50' },
  { building: 'farm', count: 100, achievementId: 'farm_100' },
  { building: 'farm', count: 150, achievementId: 'farm_150' },
  { building: 'farm', count: 200, achievementId: 'farm_200' },
  { building: 'mine', count: 1, achievementId: 'mine_1' },
  { building: 'mine', count: 50, achievementId: 'mine_50' },
  { building: 'mine', count: 100, achievementId: 'mine_100' },
  { building: 'mine', count: 150, achievementId: 'mine_150' },
  { building: 'factory', count: 1, achievementId: 'factory_1' },
  { building: 'factory', count: 50, achievementId: 'factory_50' },
  { building: 'factory', count: 100, achievementId: 'factory_100' },
  { building: 'bank', count: 1, achievementId: 'bank_1' },
  { building: 'bank', count: 50, achievementId: 'bank_50' },
  { building: 'bank', count: 100, achievementId: 'bank_100' },
  { building: 'temple', count: 1, achievementId: 'temple_1' },
  { building: 'temple', count: 50, achievementId: 'temple_50' },
  { building: 'wizard_tower', count: 1, achievementId: 'wizard_tower_1' },
  { building: 'wizard_tower', count: 50, achievementId: 'wizard_tower_50' },
  { building: 'shipment', count: 1, achievementId: 'shipment_1' },
  { building: 'shipment', count: 50, achievementId: 'shipment_50' },
  { building: 'alchemy_lab', count: 1, achievementId: 'alchemy_lab_1' },
  { building: 'portal', count: 1, achievementId: 'portal_1' },
  { building: 'time_machine', count: 1, achievementId: 'time_machine_1' },
  { building: 'antimatter_condenser', count: 1, achievementId: 'antimatter_condenser_1' },
  { building: 'prism', count: 1, achievementId: 'prism_1' },
];

export class AchievementSystem {
  win(state: GameState, achievementId: string): boolean {
    if (state.achievements.has(achievementId)) return false;
    const def = this.getDef(achievementId);
    if (!def) return false;

    state.achievements.add(achievementId);
    eventBus.emit('achievement:won', achievementId, def);
    eventBus.emit('gains:recalculate');
    return true;
  }

  check(state: GameState): void {
    for (const t of BAKE_THRESHOLDS) {
      if (state.cookiesEarned.gte(t.amount)) {
        this.win(state, t.id);
      }
    }

    for (const t of CLICK_THRESHOLDS) {
      if (state.cookieClicks >= t.amount) {
        this.win(state, t.id);
      }
    }

    for (const t of BUILDING_THRESHOLDS) {
      const bs = state.buildings.get(t.building);
      if (bs && bs.amount >= t.count) {
        this.win(state, t.achievementId);
      }
    }

    this.checkCollectionAchievements(state);
  }

  getMilkProgress(state: GameState): number {
    let normal = 0;
    for (const id of state.achievements) {
      const def = this.getDef(id);
      if (def && def.pool === 'normal') normal++;
    }
    return normal * 0.04;
  }

  getDef(id: string): AchievementDef | undefined {
    return ACHIEVEMENTS.find((a) => a.id === id);
  }

  private checkCollectionAchievements(state: GameState): void {
    const allHaveAtLeast = (n: number) => {
      return BUILDINGS.every((def) => {
        const bs = state.buildings.get(def.id);
        return bs && bs.amount >= n;
      });
    };

    if (allHaveAtLeast(1)) this.win(state, 'mathematician');
    if (allHaveAtLeast(10)) this.win(state, 'base_10');
    if (allHaveAtLeast(100)) this.win(state, 'centennial');
  }
}
